import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  useChannel,
  useDeleteChannel,
  useLeaveChannel,
  useUpdateChannel,
} from '../lib/queries'
import { toast } from '../lib/toast'
import ModManager from '../components/ModManager'
// Twitter tracked handles artık overlay seviyesinde (twitter-feed-auto state). Bu sayede
// her overlay kendi takip listesini tutar, OBS'de açıksa rule kurulur — kapalıysa sıfır
// maliyet. ChannelSettings'ten kaldırıldı.
import AppShell from '../components/AppShell'
import Section from '../components/ui/Section'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import { SkeletonCard } from '../components/ui/Skeleton'

export default function ChannelSettings() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: channel, isLoading } = useChannel(id)
  const updateChannel = useUpdateChannel(id || '')
  const deleteChannel = useDeleteChannel(id || '')
  const leaveChannel = useLeaveChannel(id || '')

  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)

  function reconnectKick() {
    const token = localStorage.getItem('auth.token')
    if (!token) return
    const apiBase = import.meta.env.VITE_API_BASE || 'http://localhost:8080'
    window.location.href = `${apiBase}/api/auth/kick/start?token=${encodeURIComponent(token)}`
  }

  function patch(body: Record<string, unknown>) {
    updateChannel.mutate(body, {
      onError: (e) => toast.error((e as Error).message),
    })
  }

  if (isLoading) {
    return (
      <AppShell activeChannelId={id || null}>
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-4">
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </AppShell>
    )
  }

  if (!channel) {
    return (
      <AppShell activeChannelId={id || null}>
        <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-[var(--color-text-muted)]">
          Kanal bulunamadı.
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell activeChannelId={id || null}>
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        <div>
          <button
            onClick={() => navigate(`/channels/${channel.id}`)}
            className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-2"
          >
            ← {channel.slug}
          </button>
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold">Kanal Ayarları</h1>
            <Badge tone={channel.is_owner ? 'accent' : 'neutral'}>{channel.role}</Badge>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">
            Bot davranışı, üyeler ve güvenlik
          </p>
        </div>

        <Section title="Bot" description="Chat komutları, yanıt yazma, link takibi">
          <div className="space-y-2.5">
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channel.bot_enabled}
                onChange={() => patch({ bot_enabled: !channel.bot_enabled })}
                disabled={!channel.is_owner}
                className="accent-[var(--color-accent)]"
              />
              <span>Komutları dinle</span>
              {!channel.is_owner && (
                <span className="text-xs text-[var(--color-text-subtle)]">(owner)</span>
              )}
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channel.bot_reply}
                onChange={() => patch({ bot_reply: !channel.bot_reply })}
                disabled={!channel.is_owner || channel.oauth_status !== 'ok'}
                className="accent-[var(--color-accent)]"
              />
              <span>Chat'e yanıt yaz</span>
              {!channel.is_owner && (
                <span className="text-xs text-[var(--color-text-subtle)]">(owner)</span>
              )}
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channel.link_tracking_enabled}
                onChange={() =>
                  patch({
                    link_tracking_enabled: !channel.link_tracking_enabled,
                    // Buradan manuel toggle: auto modu kapatma davranışı LinksPage
                    // ile tutarlı olsun. Settings'ten kapanırsa da auto'dan çıkar.
                    link_tracking_auto_managed: false,
                  })
                }
                disabled={!channel.is_owner}
                className="accent-[var(--color-accent)]"
              />
              <span>Chat'teki linkleri takip et</span>
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer pl-6">
              <input
                type="checkbox"
                checked={channel.link_tracking_auto_managed}
                onChange={() => patch({ link_tracking_auto_managed: !channel.link_tracking_auto_managed })}
                disabled={!channel.is_owner}
                className="accent-[var(--color-accent)]"
              />
              <span className="text-[var(--color-text-muted)]">
                Otomatik mod
                <span className="ml-1 text-xs text-[var(--color-text-subtle)]">
                  (yayın açıldığında otomatik başlat, 2 saat offline kalınca durdur)
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2.5 text-sm cursor-pointer pl-6">
              <input
                type="checkbox"
                checked={channel.link_dup_reply_enabled}
                onChange={() => patch({ link_dup_reply_enabled: !channel.link_dup_reply_enabled })}
                disabled={!channel.is_owner || !channel.link_tracking_enabled || !channel.bot_reply || channel.oauth_status !== 'ok'}
                className="accent-[var(--color-accent)]"
              />
              <span className="text-[var(--color-text-muted)]">
                Okunmuş linkler tekrar paylaşılırsa chat'e cevap ver
                <span className="ml-1 text-xs text-[var(--color-text-subtle)]">(yanıt yazma + link takibi açık olmalı)</span>
              </span>
            </label>

            {channel.oauth_status === 'expired' && (
              <div className="px-3 py-2 rounded bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 text-xs flex items-center justify-between">
                <span className="text-[var(--color-warning)]">OAuth süresi dolmuş</span>
                {channel.is_owner && (
                  <Button variant="ghost" size="sm" onClick={reconnectKick}>
                    Yeniden Bağla
                  </Button>
                )}
              </div>
            )}
            {channel.oauth_status === 'invalid' && (
              <div className="px-3 py-2 rounded bg-[var(--color-danger)]/10 border border-[var(--color-danger)]/30 text-xs flex items-center justify-between">
                <span className="text-[var(--color-danger)]">OAuth scope eksik</span>
                {channel.is_owner && (
                  <Button variant="ghost" size="sm" onClick={reconnectKick}>
                    Yeniden Bağla
                  </Button>
                )}
              </div>
            )}
          </div>
        </Section>

        <HighlightedAuthorsSection channel={channel} patch={patch} />


        <ModManager channelId={channel.id} isOwner={channel.is_owner} />

        {channel.is_owner && (
          <Section title="Aktivite log">
            <p className="text-sm text-[var(--color-text-muted)] mb-3">
              Kanaldaki tüm aksiyonlar (mod ekleme, OAuth, overlay create/delete vs.) burada listelenir.
            </p>
            <Button variant="secondary" onClick={() => navigate(`/channels/${channel.id}/audit`)}>
              Audit log →
            </Button>
          </Section>
        )}

        {channel.is_owner && (
          <Section title="Tehlikeli alan">
            <div className="flex gap-2 flex-wrap">
              <Button variant="secondary" onClick={() => setConfirmLeave(true)}>
                Kanalı bırak (overlay'ler korunur)
              </Button>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Kanalı sil
              </Button>
            </div>
          </Section>
        )}
      </div>

      <ConfirmDialog
        open={confirmLeave}
        onClose={() => setConfirmLeave(false)}
        onConfirm={async () => {
          try {
            await leaveChannel.mutateAsync()
            toast.success('Kanaldan çıkıldı')
            navigate('/dashboard')
          } catch (e) {
            toast.error((e as Error).message)
          }
        }}
        title="Kanalı bırak?"
        description={`${channel.slug} kanalını bırakırsın. Overlay'ler ve linkler silinmez ama owner statün düşer.`}
        confirmLabel="Bırak"
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await deleteChannel.mutateAsync()
            toast.success('Kanal silindi')
            navigate('/dashboard')
          } catch (e) {
            toast.error((e as Error).message)
          }
        }}
        title={`${channel.slug} kanalını sil?`}
        description="Bu işlem geri alınamaz. Tüm overlay'ler, linkler ve audit log silinir."
        confirmLabel="Kalıcı olarak sil"
        destructive
      />
    </AppShell>
  )
}

interface HighlightProps {
  channel: import('../types').Channel
  patch: (body: Record<string, unknown>) => void
}

function HighlightedAuthorsSection({ channel, patch }: HighlightProps) {
  const [draft, setDraft] = useState('')

  function normalize(raw: string): string {
    return raw.trim().replace(/^@/, '').toLowerCase()
  }

  function add() {
    const v = normalize(draft)
    if (!v) return
    if (channel.highlighted_twitter_authors.includes(v)) {
      setDraft('')
      return
    }
    patch({ highlighted_twitter_authors: [...channel.highlighted_twitter_authors, v] })
    setDraft('')
  }

  function remove(handle: string) {
    patch({
      highlighted_twitter_authors: channel.highlighted_twitter_authors.filter((h) => h !== handle),
    })
  }

  return (
    <Section
      title="Önemli Twitter yazarları"
      description="Belirlediğin Twitter yazarlarının paylaştığı linkler altın renkle vurgulanır"
    >
      <label className="flex items-center gap-2.5 text-sm cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={channel.highlight_authors_enabled}
          onChange={() => patch({ highlight_authors_enabled: !channel.highlight_authors_enabled })}
          disabled={!channel.is_owner}
          className="accent-[var(--color-accent)]"
        />
        <span>Vurgulama aktif</span>
      </label>

      {channel.is_owner && (
        <form
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault()
            add()
          }}
          className="flex gap-2 mb-3"
        >
          <Input
            placeholder="@kullaniciAdi"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1"
            disabled={!channel.highlight_authors_enabled}
          />
          <Button type="submit" variant="primary" disabled={!channel.highlight_authors_enabled || !draft.trim()}>
            Ekle
          </Button>
        </form>
      )}

      {channel.highlighted_twitter_authors.length === 0 ? (
        <p className="text-xs text-[var(--color-text-subtle)]">Henüz yazar eklenmemiş.</p>
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {channel.highlighted_twitter_authors.map((h) => (
            <li
              key={h}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-xs"
            >
              <span>★ @{h}</span>
              {channel.is_owner && (
                <button
                  onClick={() => remove(h)}
                  title="Kaldır"
                  className="ml-0.5 hover:text-amber-100"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
